// The time window the Triage tab reads within its loaded pool. The pool is
// the newest findings in the scope's range, up to its step, so "all loaded"
// can cover less of the range than the range picker says; the window names
// what the pool covers and narrows it: a preset back from the newest loaded
// finding, or any span dragged on the pool's own timeline. `null` is the
// whole pool.
import { parseServerTimestamp } from "./traceUtils.js";

const str = (v) => (v == null ? "" : String(v));
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// A review's completion time as the analytics rows carry it (UTC, zoned or not).
export function completedMs(row) {
    const ms = parseServerTimestamp(row?.completed_at);
    return Number.isFinite(ms) ? ms : null;
}

// What the pool covers: its oldest and newest completion, its findings and
// sessions. Rows without a readable time count but place nowhere on the line.
export function poolSpan(rows = []) {
    let fromMs = null;
    let toMs = null;
    const sessions = new Set();
    for (const row of rows) {
        sessions.add(str(row?.trace_id));
        const ms = completedMs(row);
        if (ms == null) continue;
        fromMs = fromMs == null ? ms : Math.min(fromMs, ms);
        toMs = toMs == null ? ms : Math.max(toMs, ms);
    }
    return { fromMs, toMs, findings: rows.length, sessions: sessions.size };
}

// A span as the control words it: "13d", "18h", "45m".
export function spanLabel(ms) {
    if (!Number.isFinite(ms) || ms <= 0) return "0m";
    if (ms >= 2 * DAY) return `${Math.round(ms / DAY)}d`;
    if (ms >= 2 * HOUR) return `${Math.round(ms / HOUR)}h`;
    return `${Math.max(1, Math.round(ms / MINUTE))}m`;
}

// The presets, each back from the newest loaded finding rather than from now,
// so a range that ended in the past still has a last day. A preset reaching
// past the pool's oldest finding would be the whole pool again; none is offered.
export const HOUR_PRESETS = [["1h", HOUR], ["6h", 6 * HOUR], ["24h", DAY]];
export const DAY_PRESETS = [["3d", 3 * DAY], ["7d", 7 * DAY]];
export function presetWindow(span, lengthMs) {
    if (span?.toMs == null || span?.fromMs == null) return null;
    return { fromMs: Math.max(span.fromMs, span.toMs - lengthMs), toMs: span.toMs };
}
export function presetFits(span, lengthMs) {
    return span?.toMs != null && span?.fromMs != null && span.toMs - span.fromMs > lengthMs;
}
// Which preset a window is, when it is one.
export function presetOf(span, window) {
    if (!window || span?.toMs == null || window.toMs !== span.toMs) return null;
    for (const [id, length] of [...HOUR_PRESETS, ...DAY_PRESETS]) {
        if (window.fromMs === Math.max(span.fromMs, span.toMs - length)) return id;
    }
    return null;
}

export function inWindow(row, window) {
    if (!window) return true;
    const ms = completedMs(row);
    return ms != null && ms >= window.fromMs && ms <= window.toMs;
}
export function rowsInWindow(rows = [], window = null) {
    return window ? rows.filter((row) => inWindow(row, window)) : rows;
}
export function windowCounts(rows = [], window = null) {
    const kept = rowsInWindow(rows, window);
    return { findings: kept.length, sessions: new Set(kept.map((row) => str(row?.trace_id))).size };
}

// Findings per bucket across the span, for the line the window is dragged on.
// The bucket a row falls in across the span, or null when it places nowhere.
export function bucketOf(row, span, count) {
    if (span?.fromMs == null || span?.toMs == null) return null;
    const ms = completedMs(row);
    if (ms == null) return null;
    const width = Math.max(1, span.toMs - span.fromMs);
    return Math.min(count - 1, Math.max(0, Math.floor(((ms - span.fromMs) / width) * count)));
}
export function windowBuckets(rows = [], span = null, count = 96) {
    const buckets = new Array(count).fill(0);
    for (const row of rows) {
        const i = bucketOf(row, span, count);
        if (i != null) buckets[i] += 1;
    }
    return buckets;
}

// A dragged span as a window: ordered, inside the pool, at least a minute
// wide (or the whole pool, when that is shorter), and none at all when it
// covers the whole pool. A window too narrow at the pool's end grows back
// from the end rather than past it.
export function windowFromFractions(span, a, b) {
    if (span?.fromMs == null || span?.toMs == null) return null;
    const lo = Math.max(0, Math.min(a, b));
    const hi = Math.min(1, Math.max(a, b));
    if (lo <= 0 && hi >= 1) return null;
    const width = span.toMs - span.fromMs;
    const floor = Math.min(MINUTE, width);
    let fromMs = Math.round(span.fromMs + lo * width);
    let toMs = Math.round(span.fromMs + hi * width);
    if (toMs - fromMs < floor) {
        toMs = Math.min(span.toMs, fromMs + floor);
        fromMs = toMs - floor;
    }
    return fromMs <= span.fromMs && toMs >= span.toMs ? null : { fromMs, toMs };
}
export function windowFractions(span, window) {
    if (!window || span?.fromMs == null || span?.toMs == null) return [0, 1];
    const width = Math.max(1, span.toMs - span.fromMs);
    return [Math.max(0, (window.fromMs - span.fromMs) / width), Math.min(1, (window.toMs - span.fromMs) / width)];
}

// A smooth line through points in x order that never swings past its
// neighbours (monotone cubic interpolation), so a quiet stretch stays on the
// baseline instead of dipping under it beside a spike.
export function monotonePath(points = []) {
    const n = points.length;
    if (!n) return "";
    const f = (v) => Number(v.toFixed(2));
    if (n === 1) return `M${f(points[0][0])},${f(points[0][1])}`;
    const dx = [];
    const slope = [];
    for (let i = 0; i < n - 1; i++) {
        dx.push(points[i + 1][0] - points[i][0]);
        slope.push(dx[i] ? (points[i + 1][1] - points[i][1]) / dx[i] : 0);
    }
    const tangent = new Array(n);
    tangent[0] = slope[0];
    tangent[n - 1] = slope[n - 2];
    for (let i = 1; i < n - 1; i++) {
        if (slope[i - 1] * slope[i] <= 0) { tangent[i] = 0; continue; }
        const w1 = 2 * dx[i] + dx[i - 1];
        const w2 = dx[i] + 2 * dx[i - 1];
        tangent[i] = (w1 + w2) / (w1 / slope[i - 1] + w2 / slope[i]);
    }
    let d = `M${f(points[0][0])},${f(points[0][1])}`;
    for (let i = 0; i < n - 1; i++) {
        const [x0, y0] = points[i];
        const [x1, y1] = points[i + 1];
        const h = dx[i] / 3;
        d += `C${f(x0 + h)},${f(y0 + tangent[i] * h)} ${f(x1 - h)},${f(y1 - tangent[i + 1] * h)} ${f(x1)},${f(y1)}`;
    }
    return d;
}
